'use client';
import React, { createContext, useContext, useState, useEffect } from 'react';
import { modalUtils } from '@/utils/modalUtils';
import { MODAL_CONFIGS } from './config';
import { heapTrack } from '@/lib/heap/api';

const ModalContext = createContext();

/**
 * Enum of modal types such as 'priceDetails', 'cartCrossSell' to prevent typos.
 * Example usage: showModal(MODAL_TYPES.priceDetails, { ... });
 */
export const MODAL_TYPES = Object.keys(MODAL_CONFIGS).reduce((acc, key) => {
  acc[key] = key;
  return acc;
}, {});

// Global close button factory - creates close buttons once per modal type
const createCloseButtonFactories = () => {
  const closeButtonFactories = {};

  Object.keys(MODAL_CONFIGS).forEach((modalType) => {
    const config = MODAL_CONFIGS[modalType];
    closeButtonFactories[modalType] = (onClose) =>
      modalUtils.createCloseButton(
        modalType,
        onClose,
        config.closeButton.variant,
        config.closeButton.strokeColor
      );
  });

  return closeButtonFactories;
};

// Global close button instances
const closeButtonFactoriesByModalType = createCloseButtonFactories();

export function ModalProvider({ children }) {
  const [modals, setModals] = useState({});

  const hideModal = (modalType) => {
    setModals((prev) => {
      const modalProps = prev?.[modalType]?.props;

      // Focus management for price details
      if (
        modalType === MODAL_TYPES.priceDetails &&
        modalProps?.modalOpenTrigger?.current
      ) {
        modalProps?.modalOpenTrigger?.current?.focus();
      }

      const result = { ...prev };
      delete result[modalType];
      return result;
    });
  };

  // Check if any modal is currently open
  const isAnyModalOpen = Object.values(modals).some(
    (modal) => modal?.isVisible
  );

  // Global scroll lock management - handles scroll locking when any modal is open
  useEffect(() => {
    if (typeof window === 'undefined') return;

    if (isAnyModalOpen) {
      // Calculate scrollbar width to avoid page shift
      const scrollbarWidth =
        window.innerWidth - document.documentElement.clientWidth;
      document.body.classList.add('overflow-hidden');
      document.body.style.paddingRight =
        scrollbarWidth > 0 ? `${scrollbarWidth}px` : '';
    } else {
      document.body.classList.remove('overflow-hidden');
      document.body.style.paddingRight = '';
    }

    return () => {
      // Cleanup on unmount
      document.body.classList.remove('overflow-hidden');
      document.body.style.paddingRight = '';
    };
  }, [isAnyModalOpen]);

  // Global escape key handling - closes the most recently opened modal
  useEffect(() => {
    if (!isAnyModalOpen) return;

    const handleGlobalKeyDown = (e) => {
      if (e.key === 'Escape') {
        // Find the most recently opened modal and close it
        const openModalEntries = Object.entries(modals).filter(
          ([_, modal]) => modal.isVisible
        );
        if (openModalEntries.length > 0) {
          const [lastOpenModalType] =
            openModalEntries[openModalEntries.length - 1];

          // Some modals may handle Escape key themselves, so skip if config says so
          if (MODAL_CONFIGS[lastOpenModalType]?.handlesEscapeKey) {
            return;
          }

          hideModal(lastOpenModalType);
          heapTrack('Leesa Modal Closed', {
            'Leesa - Modal Type': lastOpenModalType,
            'Leesa - Modal Close Method': 'Escape Key'
          });
        }
      }
    };

    document.addEventListener('keydown', handleGlobalKeyDown);
    return () => {
      document.removeEventListener('keydown', handleGlobalKeyDown);
    };
  }, [isAnyModalOpen, modals]);

  const showModal = (modalType, props = {}) => {
    setModals((prev) => ({
      ...prev,
      [modalType]: { isVisible: true, props }
    }));
  };

  // Legacy method
  const showPriceDetails = (product, modalOpenTrigger, content) => {
    showModal('priceDetails', {
      title: 'Price details',
      product,
      modalOpenTrigger,
      content
    });
  };

  // Get close button for specific modal type from global instances
  const getCloseButton = (modalType, onClose) => {
    const buttonFactory = closeButtonFactoriesByModalType[modalType];
    if (!buttonFactory) {
      // Fallback to basic config if modal type not found
      return modalUtils.createCloseButton(
        modalType,
        onClose,
        'basic',
        '#000000'
      );
    }
    return buttonFactory(onClose);
  };

  return (
    <ModalContext.Provider
      value={{
        showPriceDetails,
        showModal,
        hideModal: (type) => hideModal(type),

        // Global modal state
        isAnyModalOpen,
        modals,

        // Utility functions for modal components
        modalUtils,
        getCloseButton
      }}
    >
      {children}

      {/* Render all active modals */}
      {Object.entries(modals).map(([modalType, modal]) => {
        if (!modal?.isVisible) return null;

        const ModalComponent = MODAL_CONFIGS[modalType]?.Component;
        if (!ModalComponent) return null;

        return (
          <ModalComponent
            key={modalType}
            {...modal?.props}
            isOpen={modal.isVisible}
            onClose={() => hideModal(modalType)}
          />
        );
      })}
    </ModalContext.Provider>
  );
}

export function useModal() {
  return useContext(ModalContext);
}
